import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  Grid,
  GridItem,
  Input,
  Text,
  Tooltip,
  useTheme,
} from '@chakra-ui/react';

import { Field, Formik, FieldArray, useFormikContext } from 'formik';
import { useQueryClient } from '@tanstack/react-query';
import { Select } from 'chakra-react-select';

import * as Yup from 'yup';
import { MetricFilterState, MetricState } from '../../tableComponents';
import { useToastHook } from '../../appToast';
import { truncateText } from '../../shared/utils';
import { TrashIcon } from '../../icons';

export const validationSchema = Yup.object().shape({
  metricFilterResult: Yup.array().of(
    Yup.object().shape({
      metricOperator: Yup.string().required('Operator is required'),
      targetValue: Yup.string().required('Target value is required'),
    })
  ),
});
export const generateId = (prefix: string) => {
  return `${prefix}_${Math.random().toString(36).substring(2, 9)}`;
};
export const operatorOptions = [
  { value: 'GREATER_THAN', label: 'More than' },
  { value: 'LESS_THAN', label: 'Less than' },
  { value: 'BETWEEN', label: 'Between' },
];

export const GROUP_LEVEL_METRICS = [
  'TOP_REVIEWER_DISTRIBUTION',
  'CONTRIBUTOR_COUNT',
  'DEPLOYMENT_FREQUENCY',
  'MEAN_TIME_TO_RECOVERY',
  'CHANGE_FAILURE_RATE',
  'SPRINT_SCOPE_CHANGE',
  'DOCUMENT_UPDATED',
  'CHANGE_FAILURE',
];

const getInitialValues = (
  selectedMetrics: MetricState[],
  metricFilterState: MetricFilterState[],
  setMetricFilterState: any
) => {
  const existingFilterMap = new Map(
    (metricFilterState || []).map((kr) => [kr.metricName, kr])
  );

  const metricFilterResult = selectedMetrics
    .filter((kr) => {
      if (
        ['KEY_RESULTS', 'GOALS', 'TOTAL_WORKLOAD', 'ACTIVE_WORKLOAD'].includes(
          kr.metricKey
        )
      ) {
        return false;
      }
      return true;
    })
    .map((kr) => {
      const existing = existingFilterMap.get(kr.metricKey);

      return {
        id: existing?.id || generateId('kr'),
        title: kr.metricTitle || '',
        metricName: kr.metricKey || '',
        metricOperator: existing?.metricOperator || '',
        targetValue:
          existing?.targetValue === 0 ? 0 : existing?.targetValue || '',
      };
    });

  setMetricFilterState(metricFilterResult);

  return { metricFilterResult };
};

interface MetricAdvancedFilterProps {
  metricsList: any;
  selectedMetrics: any;
  metricFilterState: MetricFilterState[];
  setMetricFilterState: any;
  errorMetricFields: MetricFilterState[];
  setErrorMetricFields: any;
}

function MetricAdvancedFilter({
  metricsList,
  selectedMetrics,
  metricFilterState,
  setMetricFilterState,
  errorMetricFields,
  setErrorMetricFields,
}: MetricAdvancedFilterProps) {
  const { colors } = useTheme();
  const [newToast] = useToastHook();
  const queryClient = useQueryClient();

  const formRef = useRef<any>(null);
  const initialValues = useMemo(
    () =>
      getInitialValues(
        selectedMetrics,
        metricFilterState,
        setMetricFilterState
      ),
    [selectedMetrics]
  );

  useEffect(() => {
    if (formRef.current) {
      formRef.current.resetForm({ values: initialValues });
    }
  }, [initialValues]);
  return (
    <Formik
      key={'formKey'}
      innerRef={formRef}
      initialValues={initialValues}
      onSubmit={() => {
        return;
      }}
      validationSchema={validationSchema}
    >
      {(props) => {
        const { handleSubmit, setFieldValue, values, touched, errors } = props;

        return (
          <form onSubmit={handleSubmit}>
            <FieldArray name="metricFilterResult">
              {({ push, remove }) => {
                return (
                  <Flex direction={'column'} gap={3}>
                    {values.metricFilterResult?.map((_: any, index: any) => {
                      const isError = errorMetricFields.find(
                        (error) =>
                          error.metricName ===
                          values.metricFilterResult[index].metricName
                      );
                      let errorMessage = '';

                      if (isError) {
                        if (!isError.metricOperator) {
                          errorMessage = 'Comparison operator is required';
                        } else if (isError.metricOperator === 'BETWEEN') {
                          const min = isError.targetValue
                            ?.toString()
                            .split('-')[0];
                          const max = isError.targetValue
                            ?.toString()
                            .split('-')[1];
                          if (!min && !max) {
                            errorMessage = 'Min and max values are required';
                          } else if (!min) {
                            errorMessage = 'Min value is required';
                          } else if (!max) {
                            errorMessage = 'Max value is required';
                          } else if (
                            min &&
                            max &&
                            parseInt(min) > parseInt(max)
                          ) {
                            errorMessage =
                              'Min value must be less than max value';
                          } else if (
                            min &&
                            max &&
                            parseInt(min) === parseInt(max)
                          ) {
                            errorMessage =
                              'Min and max values must be different';
                          }
                        } else if (!isError.targetValue) {
                          errorMessage = 'Target value is required';
                        }
                      }

                      return (
                        <Box
                          key={values.metricFilterResult[index].id}
                          borderColor={'text.tertiary'}
                          rounded={'8px'}
                        >
                          <Flex align="flex-end" gap={2}>
                            <Flex direction={'column'} gap={1}>
                              <Tooltip
                                hasArrow
                                isDisabled={
                                  values.metricFilterResult[index].title
                                    .length < 20
                                }
                                label={values.metricFilterResult[index].title}
                              >
                                <Text
                                  fontSize="xs"
                                  fontFamily="heading"
                                  fontWeight="medium"
                                  color="text.secondary"
                                >
                                  {truncateText(
                                    values.metricFilterResult[index].title,
                                    20
                                  )}
                                </Text>
                              </Tooltip>
                              <Field
                                name={`metricFilterResult.${index}.metricOperator`}
                              >
                                {({ field, form }: any) => (
                                  <FormControl
                                    id="metricOperator"
                                    isInvalid={
                                      isError && !isError.metricOperator
                                    }
                                  >
                                    <Select
                                      name={`metricFilterResult.${index}.metricOperator`}
                                      placeholder="Operator"
                                      size="sm"
                                      options={operatorOptions}
                                      value={
                                        operatorOptions?.find(
                                          (option: any) =>
                                            option?.value ===
                                            values?.metricFilterResult[index]
                                              .metricOperator
                                        ) || null
                                      }
                                      onChange={(option: any) => {
                                        const existingFilter =
                                          values.metricFilterResult[index];
                                        setErrorMetricFields(
                                          (prev: MetricFilterState[]) => {
                                            return prev.filter(
                                              (error) =>
                                                error.metricName !==
                                                existingFilter.metricName
                                            );
                                          }
                                        );
                                        setMetricFilterState(
                                          (prev: MetricFilterState[]) => {
                                            return prev.map(
                                              (filter: MetricFilterState) =>
                                                filter.id === existingFilter.id
                                                  ? {
                                                      ...filter,
                                                      metricOperator:
                                                        option?.value,
                                                    }
                                                  : filter
                                            );
                                          }
                                        );
                                        setFieldValue(
                                          `metricFilterResult.${index}.metricOperator`,
                                          option?.value || ''
                                        );
                                      }}
                                      chakraStyles={{
                                        container: (provided: any) => ({
                                          ...provided,
                                          width: '130px',
                                        }),

                                        menu: (provided: any) => ({
                                          ...provided,
                                          zIndex: 999,
                                        }),

                                        control: (provided: any) => ({
                                          ...provided,
                                          zIndex: 2,
                                          borderColor: colors.text.tertiary,
                                          boxShadow: 'none',
                                          borderRadius: '4px',
                                          minHeight: '40px',
                                          '&:hover': {
                                            borderColor: 'gray.300',
                                          },
                                        }),
                                        dropdownIndicator: (provided: any) => ({
                                          ...provided,
                                          backgroundColor: 'transparent',
                                          padding: '8px',
                                        }),
                                      }}
                                      components={{
                                        IndicatorSeparator: () => null,
                                      }}
                                    />
                                  </FormControl>
                                )}
                              </Field>
                            </Flex>

                            <Flex direction={'column'} gap={1} flex={1} mr={1}>
                              <Flex justifyContent={'space-between'}>
                                {values.metricFilterResult[index]
                                  ?.metricName ? (
                                  (() => {
                                    const metric = metricsList?.dto?.find(
                                      (metric: any) =>
                                        metric?.name ===
                                          values?.metricFilterResult[index]
                                            ?.metricName ||
                                        (values?.metricFilterResult[index]
                                          ?.metricName === metric?.title &&
                                          metric?.name === 'CUSTOM')
                                    );
                                    const yLabel = metric?.y_label;
                                    const avgLabel = metric?.average_label;

                                    if (yLabel && avgLabel) {
                                      return (
                                        <Tooltip
                                          hasArrow
                                          label={
                                            <Text>
                                              {avgLabel}
                                              <Text>({yLabel})</Text>
                                            </Text>
                                          }
                                          isDisabled={avgLabel.length < 25}
                                        >
                                          <Text
                                            fontSize="xs"
                                            fontFamily="heading"
                                            fontWeight="medium"
                                            alignSelf={'end'}
                                            color="text.secondary"
                                          >
                                            {truncateText(avgLabel, 25)}{' '}
                                            {avgLabel.length < 25 && (
                                              <Text
                                                as={'span'}
                                                fontSize={'xs'}
                                                fontWeight={'medium'}
                                              >
                                                ({yLabel})
                                              </Text>
                                            )}
                                          </Text>
                                        </Tooltip>
                                      );
                                    }
                                    return (
                                      <Text
                                        fontSize="xs"
                                        fontFamily="heading"
                                        fontWeight="medium"
                                        alignSelf={'end'}
                                        color="text.secondary"
                                      >
                                        {yLabel || avgLabel || 'Target Value'}
                                      </Text>
                                    );
                                  })()
                                ) : (
                                  <Text
                                    fontSize="xs"
                                    fontFamily="heading"
                                    fontWeight="medium"
                                    alignSelf={'end'}
                                    color="text.secondary"
                                  >
                                    'Target Value'
                                  </Text>
                                )}

                                <Tooltip label="Clear Filter" hasArrow>
                                  <Button
                                    alignSelf={'end'}
                                    size="xs"
                                    variant="unstyled"
                                    onClick={() => {
                                      const existingFilter =
                                        values.metricFilterResult[index];

                                      setErrorMetricFields(
                                        (prev: MetricFilterState[]) => {
                                          return prev.filter(
                                            (error) =>
                                              error.metricName !==
                                              existingFilter.metricName
                                          );
                                        }
                                      );
                                      setMetricFilterState(
                                        (prev: MetricFilterState[]) => {
                                          return prev.map(
                                            (filter: MetricFilterState) =>
                                              filter.id === existingFilter.id
                                                ? {
                                                    ...filter,
                                                    targetValue: '',
                                                    metricOperator: '',
                                                  }
                                                : filter
                                          );
                                        }
                                      );

                                      setFieldValue(
                                        `metricFilterResult.${index}.targetValue`,
                                        ''
                                      );

                                      setFieldValue(
                                        `metricFilterResult.${index}.metricOperator`,
                                        ''
                                      );
                                    }}
                                  >
                                    <TrashIcon />
                                  </Button>
                                </Tooltip>
                              </Flex>
                              <Field
                                name={`metricFilterResult.${index}.targetValue`}
                              >
                                {({ field, form }: any) => (
                                  <FormControl
                                    id="targetValue"
                                    isInvalid={
                                      isError &&
                                      (!isError.targetValue ||
                                        isError.metricOperator === 'BETWEEN')
                                    }
                                  >
                                    {values.metricFilterResult[index]
                                      .metricOperator === 'BETWEEN' ? (
                                      <Flex align="center">
                                        <Input
                                          value={
                                            (field.value === 0
                                              ? 0
                                              : field.value || ''
                                            )
                                              .toString()
                                              .split('-')[0] || ''
                                          }
                                          onChange={(e) => {
                                            const secondValue =
                                              (field.value || '')
                                                .toString()
                                                .split('-')[1] || '';

                                            const existingFilter =
                                              values.metricFilterResult[index];

                                            setErrorMetricFields(
                                              (prev: MetricFilterState[]) => {
                                                return prev.filter(
                                                  (error) =>
                                                    error.metricName !==
                                                    existingFilter.metricName
                                                );
                                              }
                                            );

                                            setMetricFilterState(
                                              (prev: MetricFilterState[]) => {
                                                return prev.map(
                                                  (filter: MetricFilterState) =>
                                                    filter.id ===
                                                    existingFilter.id
                                                      ? {
                                                          ...filter,
                                                          targetValue: `${e.target.value}-${secondValue}`,
                                                        }
                                                      : filter
                                                );
                                              }
                                            );
                                            setFieldValue(
                                              `metricFilterResult.${index}.targetValue`,
                                              `${e.target.value}-${secondValue}`
                                            );
                                          }}
                                          placeholder="Min value"
                                          borderColor={colors.text.tertiary}
                                          width="45%"
                                          type="number"
                                          min={0}
                                          required
                                        />
                                        <Text mx={2} fontWeight="medium">
                                          -
                                        </Text>
                                        <Input
                                          value={
                                            (field.value === 0
                                              ? 0
                                              : field.value || ''
                                            )
                                              .toString()
                                              .split('-')[1] || ''
                                          }
                                          onChange={(e) => {
                                            const firstValue =
                                              (field.value || '')
                                                .toString()
                                                .split('-')[0] || '';

                                            const existingFilter =
                                              values.metricFilterResult[index];

                                            setErrorMetricFields(
                                              (prev: MetricFilterState[]) => {
                                                return prev.filter(
                                                  (error) =>
                                                    error.metricName !==
                                                    existingFilter.metricName
                                                );
                                              }
                                            );

                                            setMetricFilterState(
                                              (prev: MetricFilterState[]) => {
                                                return prev.map(
                                                  (filter: MetricFilterState) =>
                                                    filter.id ===
                                                    existingFilter.id
                                                      ? {
                                                          ...filter,
                                                          targetValue: `${firstValue}-${e.target.value}`,
                                                        }
                                                      : filter
                                                );
                                              }
                                            );
                                            setFieldValue(
                                              `metricFilterResult.${index}.targetValue`,
                                              `${firstValue}-${e.target.value}`
                                            );
                                          }}
                                          placeholder="Max value"
                                          borderColor={colors.text.tertiary}
                                          width="45%"
                                          min={0}
                                          type="number"
                                          required
                                        />
                                      </Flex>
                                    ) : (
                                      <Input
                                        {...field}
                                        value={
                                          field.value === 0
                                            ? 0
                                            : field.value || ''
                                        }
                                        onChange={(e) => {
                                          const rawValue = e.target.value;
                                          const numericValue = parseInt(
                                            rawValue,
                                            10
                                          );

                                          if (
                                            !Number.isNaN(numericValue) &&
                                            numericValue < 0
                                          ) {
                                            return;
                                          }

                                          const existingFilter =
                                            values.metricFilterResult[index];
                                          setErrorMetricFields(
                                            (prev: MetricFilterState[]) => {
                                              return prev.filter(
                                                (error) =>
                                                  error.metricName !==
                                                  existingFilter.metricName
                                              );
                                            }
                                          );
                                          setMetricFilterState(
                                            (prev: MetricFilterState[]) => {
                                              return prev.map(
                                                (filter: MetricFilterState) =>
                                                  filter.id ===
                                                  existingFilter.id
                                                    ? {
                                                        ...filter,
                                                        targetValue:
                                                          numericValue,
                                                      }
                                                    : filter
                                              );
                                            }
                                          );
                                          setFieldValue(
                                            `metricFilterResult.${index}.targetValue`,
                                            numericValue
                                          );
                                        }}
                                        placeholder="Target value"
                                        borderColor={colors.text.tertiary}
                                        type="number"
                                        min={0}
                                        required
                                      />
                                    )}
                                  </FormControl>
                                )}
                              </Field>
                            </Flex>
                          </Flex>
                          {isError && (
                            <Text
                              color="error"
                              fontSize={'xs'}
                              mt={1}
                              ml={isError.metricOperator ? '140px' : '0px'}
                            >
                              {errorMessage}
                            </Text>
                          )}
                        </Box>
                      );
                    })}
                  </Flex>
                );
              }}
            </FieldArray>
          </form>
        );
      }}
    </Formik>
  );
}

export default MetricAdvancedFilter;
